// Normalises radar / camera / sensor state for the card icons.
//
// Live data comes from GET /api/peripherals/<ip> (backend reads the cores
// directly). When the device is offline or a core is unreachable we fall back
// to the last-known columns Back4App holds: last_<kind>_model, last_<kind>_seen
// and, for sensors, the last_sensors array.

// Parse dates arrive either as ISO strings or {__type:'Date', iso}.
export const toIso = (v) => (v && typeof v === 'object' ? v.iso ?? null : v ?? null);

export const formatAgo = (iso) => {
	const t = iso ? new Date(iso).getTime() : NaN;
	if (Number.isNaN(t)) return null;
	const mins = Math.max(0, Math.round((Date.now() - t) / 60_000));
	if (mins < 1) return 'just now';
	if (mins < 60) return `${mins} min ago`;
	const hrs = Math.round(mins / 60);
	if (hrs < 48) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
	return `${Math.round(hrs / 24)} days ago`;
};

// Tone follows the Ironman UI convention:
//   active (blue)       radar transmitting (badge reads Transmitting, colour same as connected)
//   connected (blue)    connected
//   disconnected (grey) core reachable, peripheral not connected
//   none (grey)         no sensors detected — sensors are never shown red
//   unknown (grey)      no live data (device offline / core unreachable)
export const TONE_CLASS = {
	active: 'text-blue-500',
	connected: 'text-blue-500',
	disconnected: 'text-gray-400',
	none: 'text-gray-400',
	unknown: 'text-gray-400',
};

export const TONE_BADGE = {
	active: 'bg-blue-100 text-blue-700',
	connected: 'bg-blue-100 text-blue-700',
	disconnected: 'bg-gray-100 text-gray-600',
	none: 'bg-gray-100 text-gray-600',
	unknown: 'bg-gray-100 text-gray-600',
};

export const TONE_LABEL = {
	active: 'Transmitting',
	connected: 'Connected',
	disconnected: 'Disconnected',
	none: 'None',
	unknown: 'Unknown',
};

export const radarTone = (radar) => {
	if (!radar) return 'unknown';
	if (radar.transmitting) return 'active';
	if (radar.connected) return 'connected';
	return 'disconnected';
};

export const cameraTone = (camera) => {
	if (!camera) return 'unknown';
	return camera.connected ? 'connected' : 'disconnected';
};

// Blue only when the core has fresh position AND heading; a bus full of
// sensors that can't fix the vessel is still grey.
export const sensorsTone = (sensors) => {
	if (!sensors) return 'unknown';
	return sensors.position_ok === true && sensors.heading_ok === true ? 'connected' : 'none';
};

// Builds the three panel entries from live data + last-known fallbacks.
export const buildPeripherals = ({ live, devkit, hasCamera }) => {
	const out = [];
	const radar = live?.radar ?? null;
	out.push({
		kind: 'radar',
		label: 'Radar',
		tone: radarTone(radar),
		live: radar,
		model: radar?.model ?? devkit?.last_radar_model ?? null,
		lastSeen: toIso(devkit?.last_radar_seen),
		detail: radar?.ip_address || null,
	});

	if (hasCamera) {
		const camera = live?.camera ?? null;
		out.push({
			kind: 'camera',
			label: 'Camera',
			tone: cameraTone(camera),
			live: camera,
			model: camera?.model ?? devkit?.last_camera_model ?? null,
			lastSeen: toIso(devkit?.last_camera_seen),
			detail: camera?.status || null,
		});
	}

	const sensors = live?.sensors ?? null;
	const liveDevices = sensors?.devices ?? null;
	const lastDevices = Array.isArray(devkit?.last_sensors) ? devkit.last_sensors : [];
	out.push({
		kind: 'sensors',
		label: 'Sensors',
		tone: sensorsTone(sensors),
		live: sensors,
		model: null,
		lastSeen: toIso(devkit?.last_sensors_seen),
		devices: liveDevices?.length ? liveDevices : lastDevices,
		devicesAreLive: !!liveDevices?.length,
		// device_ids ProteusCore is currently using for position / heading
		selectedPos: sensors?.current_pos_instance ?? null,
		selectedHdg: sensors?.current_hdg_instance ?? null,
		detail: null,
	});

	return out;
};

// Payload for /api/update-last-known-peripherals. Only stamps *_seen when the
// peripheral is actually connected right now.
export const lastKnownPayload = (live, devkitId) => {
	const now = new Date().toISOString();
	const sensors = live?.sensors;
	return {
		devkit_id: devkitId,
		radar_model: live?.radar?.model ?? null,
		radar_seen: live?.radar?.connected ? now : null,
		camera_model: live?.camera?.model ?? null,
		camera_seen: live?.camera?.connected ? now : null,
		sensors: sensors?.devices?.length
			? sensors.devices.map((d) => ({ name: d.name, model: d.model, last_seen: now }))
			: null,
		sensors_seen: sensors?.devices?.length ? now : null,
	};
};
